import { Button, Grid, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { setTitle } from "store/reducer/appReducer";
import { SelectChangeEvent } from "@mui/material/Select";
import PageSize from "components/PageSize";
import Pagination from "components/Pagination";
import { adminService } from "services/admin/admin.service";

import Loading from "components/Loading";
import Datepicker from "components/Datepicker";
import moment from "moment";
import { Search } from "@mui/icons-material";

import CustomTableCell from "components/CustomTableCell";
import CustomTableRow from "components/CustomTableRow";
import { styles } from "assets/styles";
import { setHeader } from "helpers/utility";

const headers = setHeader([
  { name: "วันที่/เวลา", align: "center" },
  { name: "ผู้ดูแลระบบ", align: "center" },
  { name: "อีเมล", align: "center" },
  { name: "การกระทำ", align: "center" },
  { name: "บทบาท/สิทธิ์", align: "center" },
]);

const actionTypes = [
  { value: "", label: "ทั้งหมด" },
  { value: "login", label: "เข้าสู่ระบบ" },
  { value: "logout", label: "ออกจากระบบ" },
  { value: "password_change", label: "เปลี่ยนรหัสผ่าน" },
  { value: "password_reset", label: "รีเซ็ตรหัสผ่าน" },
];

const actionTypeLabels: { [key: string]: string } = {
  login: "เข้าสู่ระบบ",
  logout: "ออกจากระบบ",
  password_change: "เปลี่ยนรหัสผ่าน",
  password_reset: "รีเซ็ตรหัสผ่าน",
};

export default function SecurityLog() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const [pageCount, setPageCount] = useState("0");

  const [keyword, setKeyword] = useState("");
  const [actionType, setActionType] = useState("");

  // Use moment.js like other log pages for consistency
  const today = moment().format("YYYY-MM-DD");
  const thirtyDaysAgo = moment().add(-30, "days").format("YYYY-MM-DD");
  
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  
  const [validStartDate, setValidStartDate] = useState("");
  const [validEndDate, setValidEndDate] = useState("");

  const [dataTable, setDataTable] = useState<any>([]);

  const searchSecurityLog = async () => {
    if (!startDate || !endDate) {
      if (!startDate) setValidStartDate("valid");
      if (!endDate) setValidEndDate("valid");
      return;
    }

    if (loading) {
      console.log("Frontend debug - already loading, skipping request");
      return;
    }

    setLoading(true);
    const log = {
      keyword: keyword,
      startDate: startDate,
      endDate: endDate,
      actionType: actionType,
      page: page,
      pageSize: pageSize,
      download: false,
    };
    
    console.log("Frontend debug - sending log:", log);
    console.log("Frontend debug - startDate:", startDate, "endDate:", endDate);
    
    try {
      await adminService.searchSecurityLog(log).then((item: any) => {
        const data = item.data.data ?? item.data;
        if (item.data.count !== 0) {
          setPageCount(item.data.count || 1);
        }
        setDataTable(data);
      });
    } catch (error) {
      console.error("Error searching security log:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetPage = () => {
    setPage(1);
    setPageCount("0");
  };

  const handleKeyword = (value: any) => {
    if (keyword !== value) resetPage();
    setKeyword(value);
  };

  const handleActionType = (value: any) => {
    if (actionType !== value) resetPage();
    setActionType(value);
  };

  const handleStartDate = (value: any) => {
    if (startDate !== value) resetPage();
    setStartDate(value);
  };

  const handleEndDate = (value: any) => {
    if (endDate !== value) resetPage();
    setEndDate(value);
  };

  useEffect(() => {
    dispatch(setTitle("Security Log การใช้งานของผู้ดูแลระบบ"));
  }, [dispatch]);

  useEffect(() => {
    console.log("Frontend debug - useEffect triggered");
    console.log("Frontend debug - current startDate:", startDate);
    console.log("Frontend debug - current endDate:", endDate);
    
    if (startDate && endDate) {
      searchSecurityLog();
    }

    if (startDate) setValidStartDate("");
    if (endDate) setValidEndDate("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, keyword, actionType, startDate, endDate]);

  const handleChange = (event: SelectChangeEvent) => setPageSize(event.target.value as string);
  const handleChangePage = (value: any) => setPage(value);

  const exportExcel = async () => {
    if (!startDate || !endDate) {
      if (!startDate) setValidStartDate("valid");
      if (!endDate) setValidEndDate("valid");
      return;
    }
    setLoading(true);
    const log = {
      keyword: keyword,
      startDate: startDate,
      endDate: endDate,
      actionType: actionType,
      page: page,
      pageSize: pageSize,
      download: true,
    };
    
    console.log("Export Excel - log data:", log);
    console.log("Export Excel - startDate type:", typeof startDate, "value:", startDate);
    console.log("Export Excel - endDate type:", typeof endDate, "value:", endDate);
    
    try {
      await adminService.searchSecurityLog(log).then((response: any) => {
        console.log("Excel download response:", response);
        console.log("Response data type:", typeof response.data);
        console.log("Response headers:", response.headers);
        
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `export_security_log_${startDate}_to_${endDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error("Error exporting security log:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Loading loading={loading} />
      <Grid container alignContent="center" mb={3}>
        <Grid container justifyContent="start" item xs={12} md={8} spacing={2}>
          <Grid item sm={12} md={"auto"}>
            <Typography mt="6px" pr="20px" className="label-input">
              ค้นหา
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              value={keyword}
              onChange={(e) => handleKeyword(e.target.value)}
              className="placeholder-overflow"
              sx={{
                ml: 1,
                flex: 1,
                width: "95%",
                "& fieldset": {
                  borderRadius: `25px`,
                  border: "none",
                  boxShadow: 3,
                  height: "55px",
                },
              }}
              placeholder="ค้นหาชื่อ นามสกุล, อีเมล, บทบาท"
              InputProps={{
                endAdornment: <Search color="disabled" />,
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>ประเภทการกระทำ</InputLabel>
            <Select
              value={actionType}
              onChange={(e) => handleActionType(e.target.value)}
              label="ประเภทการกระทำ"
            >
              {actionTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container>
        <Grid container justifyContent="start" item xs={12} sm={8} md={9} lg={9} xl={10} spacing={2}>
          <Grid item sm={12} md={"auto"}>
            <Typography mt="3px" className="label-input">
              ช่วงวันที่
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} mb={2}>
            <Datepicker
              props={{
                valid: validStartDate,
                onChange: handleStartDate,
                value: startDate,
                placeholder: "วันที่เริ่มต้น",
              }}
            />
          </Grid>
          <Grid container item xs={"auto"}>
            <b
              style={{
                marginTop: "6px",
              }}
            >
              -
            </b>
          </Grid>
          <Grid item xs={12} md={4} mb={2}>
            <Datepicker
              props={{
                valid: validEndDate,
                onChange: handleEndDate,
                value: endDate,
                placeholder: "วันที่สิ้นสุด",
              }}
            />
          </Grid>

          <Grid item xs={12} md={2} mb="15px">
            <Button style={{ backgroundColor: "white" }} sx={styles.btnRoundOutline} variant="outlined" color="primary" onClick={() => searchSecurityLog()}>
              ค้นหา
            </Button>
          </Grid>
        </Grid>

        <Grid container justifyContent="end" item xs={6} md={3} lg={3} xl={2} pr={4} mb="15px">
          <Button
            onClick={() => {
              exportExcel();
            }}
            sx={styles.btnRoundExport}
            variant="contained"
            type="submit"
          >
            ส่งออก Excel
          </Button>
        </Grid>
      </Grid>
      
      <TableContainer sx={{ pt: 3 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead className="table-header">
            <TableRow>{headers}</TableRow>
          </TableHead>
          <TableBody>
            {dataTable.length > 0 ? (
              dataTable.map((row: any) => (
                <CustomTableRow key={row.id}>
                  <CustomTableCell align="center">{moment(row.createdAt).format("DD/MM/YYYY HH:mm น.") || ""}</CustomTableCell>
                  <CustomTableCell align="center">{row.adminName || `Admin ID: ${row.adminId}`}</CustomTableCell>
                  <CustomTableCell align="center">{row.adminEmail || "N/A"}</CustomTableCell>
                  <CustomTableCell align="center">{actionTypeLabels[row.actionType] || row.actionType}</CustomTableCell>
                  <CustomTableCell align="center">{row.adminRole || "N/A"}</CustomTableCell>
                </CustomTableRow>
              ))
            ) : (
              <CustomTableRow>
                <CustomTableCell colSpan={5} align="center">
                  ไม่พบข้อมูล
                </CustomTableCell>
              </CustomTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination count={parseInt(`${Math.ceil(parseInt(pageCount) / parseInt(pageSize))}`)} page={page} handleChange={handleChangePage} />
      <PageSize pageSize={pageSize} page={page} count={pageCount} handleChange={handleChange} />
    </>
  );
}
